import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Store, Star, Package, MapPin, Globe, Info, Loader2 } from "lucide-react";
import {
  CATEGORIAS_DIGITALES,
  MKT_GOLD,
  MKT_TINT,
} from "../../lib/marketplace";
import { apiGetTienda, apiGetProductos } from "../../lib/marketplaceApi";
import { EstadoBadge, SELLER_NOTICE } from "./mktui";

const catLabel = (id) => CATEGORIAS_DIGITALES.find((c) => c.id === id)?.label || id;

export default function TiendaPublica() {
  const navigate = useNavigate();
  const [tienda, setTienda] = useState(undefined);
  const [productos, setProductos] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [t, p] = await Promise.all([apiGetTienda(), apiGetProductos()]);
        setTienda(t);
        setProductos((p || []).filter((x) => x.estado === "publicado"));
      } catch (e) {
        setTienda(null);
      }
    })();
  }, []);

  if (tienda === undefined) {
    return <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  }

  if (!tienda) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Store size={40} className="mx-auto text-slate-300" />
          <p className="mt-3 text-slate-500">Aún no has creado tu tienda.</p>
          <button onClick={() => navigate("/marketplace/crear-tienda")} className="mt-4 px-5 py-2.5 rounded-xl text-white font-bold" style={{ backgroundColor: MKT_GOLD }}>
            Crear mi tienda
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="tienda-publica-page">
        <button onClick={() => navigate("/marketplace")} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-slate-800" data-testid="tp-volver">
          <ChevronLeft size={16} /> Volver a Mi Tienda
        </button>

        <div className="mt-3 inline-flex items-center gap-2 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1">
          <Info size={13} /> Vista previa pública de tu tienda
        </div>

        <div className="mt-4 rounded-3xl border border-slate-200 bg-white shadow-soft overflow-hidden">
          <div className="h-40 md:h-52" style={{ background: tienda.portada ? undefined : `linear-gradient(135deg, ${MKT_GOLD}, #B45309)` }}>
            {tienda.portada && <img src={tienda.portada} alt="portada" className="w-full h-full object-cover" />}
          </div>
          <div className="px-6 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12">
              <div className="w-24 h-24 rounded-2xl border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center flex-shrink-0">
                {tienda.logo ? <img src={tienda.logo} alt="logo" className="w-full h-full object-cover" /> : <Store size={34} style={{ color: MKT_GOLD }} />}
              </div>
              <div className="flex-1 sm:pb-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-extrabold text-slate-900">{tienda.nombre}</h1>
                  <EstadoBadge estado={tienda.estado} tipo="tienda" />
                </div>
                <p className="text-sm text-slate-500 mt-0.5">por <b className="text-slate-700">{tienda.vendedor}</b></p>
              </div>
            </div>

            {tienda.descripcion && <p className="mt-4 text-slate-600 max-w-2xl">{tienda.descripcion}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-5 text-sm text-slate-600">
              {(tienda.ciudad || tienda.pais) && <span className="inline-flex items-center gap-1.5"><MapPin size={15} style={{ color: MKT_GOLD }} /> {[tienda.ciudad, tienda.pais].filter(Boolean).join(", ")}</span>}
              {tienda.web && <a href={tienda.web} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-amber-700"><Globe size={15} style={{ color: MKT_GOLD }} /> Sitio web</a>}
              <span className="inline-flex items-center gap-1.5"><Star size={15} style={{ color: MKT_GOLD }} /> Nuevo vendedor</span>
            </div>

            {tienda.categorias?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {tienda.categorias.map((id) => (
                  <span key={id} className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: MKT_TINT, color: "#92400E" }}>{catLabel(id)}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        <h2 className="mt-8 text-lg font-extrabold text-slate-900">Productos publicados</h2>
        {productos.length === 0 ? (
          <div className="mt-3 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-400">
            <Package size={32} className="mx-auto" />
            <p className="mt-2 text-sm font-semibold">Aún no hay productos publicados en tu tienda.</p>
            <p className="text-xs mt-1">Los productos aparecen aquí una vez aprobados y publicados.</p>
          </div>
        ) : (
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {productos.map((p) => (
              <div key={p.id} className="rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden">
                <div className="h-28 flex items-center justify-center" style={{ background: p.cover ? undefined : `linear-gradient(135deg, #FFFFFF, ${MKT_TINT})` }}>
                  {p.cover ? <img src={p.cover} alt={p.titulo} className="w-full h-full object-cover" /> : <Package size={28} style={{ color: MKT_GOLD }} />}
                </div>
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase" style={{ color: MKT_GOLD }}>{catLabel(p.categoria)}</p>
                  <p className="mt-1 font-bold text-slate-900 text-sm">{p.titulo}</p>
                  <p className="mt-2 font-extrabold text-slate-900">S/ {Number(p.precio || 0).toFixed(2)}</p>
                  <p className="mt-2 text-[11px] text-slate-400 inline-flex items-center gap-1"><Info size={11} /> {SELLER_NOTICE}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
